/**
 * Data-fetching helpers. Deliberately small: one loader hook, one SSE hook.
 *
 * The SSE hook reconnects on its own because plant Wi-Fi drops connections
 * routinely, and a dashboard that silently stops updating is worse than one
 * that shows a stale marker.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import type { StreamMessage } from '../api/types';

export interface LoadState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): LoadState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcherRef
      .current()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.detail : (err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

/**
 * Subscribe to the server-sent event stream for this organisation.
 * Returns the most recent N messages plus a connection flag.
 */
export type TimedMessage = StreamMessage & { at: string };

export function useEventStream(limit = 40) {
  const [messages, setMessages] = useState<TimedMessage[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const connect = () => {
      if (closed) return;
      es = new EventSource('/api/v1/stream', { withCredentials: true });
      es.onopen = () => setConnected(true);
      es.onmessage = (ev) => {
        if (!ev.data) return; // heartbeat
        try {
          const parsed = JSON.parse(ev.data) as StreamMessage;
          const timed: TimedMessage = {
            ...parsed,
            at: new Date().toLocaleTimeString('en-GB', { hour12: false }),
          };
          setMessages((prev) => [timed, ...prev].slice(0, limit));
        } catch {
          /* ignore malformed frames rather than dropping the stream */
        }
      };
      es.onerror = () => {
        setConnected(false);
        es?.close();
        // EventSource normally retries itself; this covers hard failures.
        if (!closed) retry = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      es?.close();
    };
  }, [limit]);

  return { messages, connected };
}

/** Relative time, short form, for dense tables. */
export function ago(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
